import React, { useEffect, useMemo, useState } from "react";
import { api } from "../utils/api";
import MealCard from "../components/MealCard";
import { useI18n } from "../context/I18nContext";

export default function AllMeals({ navigate }) {
  const { t, language } = useI18n();
  const [meals, setMeals] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [mealType, setMealType] = useState("all");
  const [cuisine, setCuisine] = useState("all");

  const categories = useMemo(() => [
    { value: "all", label: t("all") },
    { value: "healthy", label: t("healthy") },
    { value: "tasty", label: t("tasty") },
    { value: "balanced", label: t("balanced") },
  ], [t]);

  const mealTypes = useMemo(() => [
    { value: "all", label: t("allMealsFilter") },
    { value: "breakfast", label: t("breakfast") },
    { value: "lunch", label: t("lunch") },
    { value: "dinner", label: t("dinner") },
  ], [t]);

  useEffect(() => {
    api.getCuisines().then((response) => setCuisines(response.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      api.getMeals({ category, mealType, cuisine, search, lang: language })
        .then((response) => setMeals(response.data.filter((meal) => meal.mealType !== "dessert")))
        .catch(console.error)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [search, category, mealType, cuisine, language]);

  return (
    <div>
      <div className="page-header">
        <h1>🍽️ {t("meals")}</h1>
        <p>{t("allMealsIntro")}</p>
      </div>

      <div className="filter-bar filter-panel">
        <div className="search-box">
          <span>🔍</span>
          <input
            type="text"
            placeholder={t("searchMeals")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search && <button onClick={() => setSearch("")}>×</button>}
        </div>

        <FilterGroup label={t("meal")} items={mealTypes} value={mealType} onChange={setMealType} />
        <FilterGroup label={t("category")} items={categories} value={category} onChange={setCategory} />

        <div className="filter-row">
          <span className="filter-label">{t("cuisine")}</span>
          <select value={cuisine} onChange={(event) => setCuisine(event.target.value)} className="select-input">
            <option value="all">{t("allWorldCuisines")}</option>
            {cuisines.map((item) => (
              <option key={item.name} value={item.name}>{item.name} ({item.recipeCount})</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /><p>{t("loadingMeals")}</p></div>
      ) : meals.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🍽️</div>
          <h3>{t("noMealFound")}</h3>
          <p>{t("tryDifferentFilters")}</p>
        </div>
      ) : (
        <>
          <p className="result-count">{meals.length} {t("mealsFound")}</p>
          <div className="meals-grid">
            {meals.map((meal, index) => (
              <MealCard
                key={meal.id}
                meal={meal}
                onClick={() => navigate("detail", meal)}
                style={{ animationDelay: `${index * 25}ms` }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FilterGroup({ label, items, value, onChange }) {
  return (
    <div className="filter-row">
      <span className="filter-label">{label}</span>
      <div className="filter-options">
        {items.map((item) => (
          <button
            key={item.value}
            className={`filter-btn ${value === item.value ? "active" : ""}`}
            onClick={() => onChange(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
